import { z } from 'zod';
import { ProvisioningMethodSchema } from './product';

const ProvisionStatusSchema = z.enum([
  'not_provisioned',
  'product_provisioned',
  'workspace_provisioning',
  'workspace_provisioned',
  'runtimeenv_provisioning',
  'fully_provisioned',
]);

export type ProvisionStatus = z.infer<typeof ProvisionStatusSchema>;

/** orch-workspaces エンドポイントのプロダクト Zod スキーマ */
const OrchWorkspacesProductSchema = z.object({
  product_id: z.string(),
  product_name: z.string(),
  provisioning_method: ProvisioningMethodSchema,
  external_product_id: z.string(),
  team: z.string(),
  budget: z.string(),
  provision_status: ProvisionStatusSchema,
});

/** Entra グループ Zod スキーマ */
const EntraGroupSchema = z.object({
  id: z.string(),
  display_name: z.string(),
});

/** App CI Zod スキーマ */
const AppCISchema = z.object({
  id: z.string(),
  parent_workspace_id: z.string(),
  path: z.string(),
});

/** ランタイム環境 Zod スキーマ */
const RuntimeEnvSchema = z.object({
  id: z.string(),
  env: z.string(),
  name: z.string(),
  parent_workspace_id: z.string(),
});

/** Azure リンク Zod スキーマ */
const AzureLinkSchema = z.object({
  subscription_id: z.string(),
  tenant_id: z.string(),
  resource_provider: z.string(),
  resource_name: z.string(),
  display_name: z.string(),
});

/** ワークスペース Zod スキーマ */
const WorkspaceSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
});

/** 処理中の AppCI Zod スキーマ */
const InProgressAppCIJobSchema = z.object({
  job_id: z.string(),
  id: z.string(),
  job_type: z.enum(['provision', 'delete']),
  // BFF は name: str | None = None（OpenAPI上は省略可）を返しうるため nullable/省略可とする
  name: z.string().nullable().optional(),
});

/** 処理中の RuntimeEnv Zod スキーマ */
const InProgressRuntimeJobSchema = z.object({
  job_id: z.string(),
  id: z.string(),
  job_type: z.enum(['provision', 'update', 'delete']),
  env: z.string(),
  env_name: z.string(),
  status: z.string(),
});

/** 処理中のリソース Zod スキーマ */
const InProgressSchema = z.object({
  runtimes: z.array(InProgressRuntimeJobSchema),
  app_cis: z.array(InProgressAppCIJobSchema),
});

/** 後方互換性のため残されている空の削除中リソース Zod スキーマ */
const DeletingSchema = z.object({});

/** ワークスペース情報アイテム Zod スキーマ */
const WorkspacesItemSchema = z.object({
  workspace: WorkspaceSchema.nullable(),
  entra_group: EntraGroupSchema.nullable(),
  app_cis: z.array(AppCISchema),
  runtimeenvs: z.array(RuntimeEnvSchema),
  azure_links: z.array(AzureLinkSchema),
  in_progress: InProgressSchema,
  deleting: DeletingSchema,
});

/**
 * GET /api/products/{product_id}/orch-workspaces レスポンスの Zod スキーマ
 */
export const OrchWorkspacesResponseSchema = z.object({
  product: OrchWorkspacesProductSchema,
  workspaces: z.array(WorkspacesItemSchema),
});

export type OrchWorkspacesProductResponse = z.infer<
  typeof OrchWorkspacesProductSchema
>;
/** ワークスペースの型 */
export type OrchWorkspaceResponse = z.infer<typeof WorkspaceSchema>;

/** EntraGroup レスポンスの型 */
export type OrchEntraGroupResponse = z.infer<typeof EntraGroupSchema>;

/** App CI の型 */
export type OrchAppCIResponse = z.infer<typeof AppCISchema>;

/** ランタイム環境の型 */
export type OrchRuntimeEnvResponse = z.infer<typeof RuntimeEnvSchema>;

/** Azure リンクの型 */
export type OrchAzureLinkResponse = z.infer<typeof AzureLinkSchema>;

/** 処理中の AppCI ジョブの型 */
export type InProgressAppCIJob = z.infer<typeof InProgressAppCIJobSchema>;

/** 処理中の RuntimeEnv ジョブの型 */
export type InProgressRuntimeJob = z.infer<typeof InProgressRuntimeJobSchema>;

/** 払い出し中のリソースの型 */
export type InProgress = z.infer<typeof InProgressSchema>;

/** 削除中のリソースの型 */
export type Deleting = z.infer<typeof DeletingSchema>;

/** ワークスペース情報アイテムの型 */
export type OrchWorkspacesItemResponse = z.infer<typeof WorkspacesItemSchema>;

/** orch-workspaces API レスポンスの型 */
export type OrchWorkspacesResponse = z.infer<
  typeof OrchWorkspacesResponseSchema
>;
